interface PaginationItem {
  isCanonical?: boolean
  isCurrent?: boolean
  isNext?: boolean
  isNextPrevious?: boolean
  isPrevious?: boolean
  link: string
  text: string
}

const getPaginationContainer = (document: Document): HTMLElement | null => {
  const paginationContainer = document.querySelector<HTMLElement>('.pagination')
  if (!paginationContainer) return null

  paginationContainer.setAttribute('aria-labelledby', 'pagination-title')
  paginationContainer.removeAttribute('aria-hidden')

  return paginationContainer
}

const getQueries = (): string => {
  const queries = new URLSearchParams(window.location.search)
  if (queries.has('page')) queries.delete('page')
  return queries.size ? `&${queries.toString()}` : ''
}

const getPaginationItem = (
  document: Document,
  { isCanonical, isCurrent, isNext, isNextPrevious, isPrevious, link, text }: PaginationItem,
): HTMLLIElement => {
  const listItemElement = document.createElement('li')
  const linkElement = document.createElement('a')
  linkElement.innerHTML = text

  let textTranslated = text
  if (text === '&lt;') {
    textTranslated = 'précédente'
  } else if (text === '&gt;') {
    textTranslated = 'suivante'
  }

  if (isCurrent) {
    linkElement.setAttribute('href', '')
    linkElement.setAttribute(
      'title',
      `Impossible de naviguer vers la page ${textTranslated} car vous êtes déjà à cette page`,
    )

    if (isNextPrevious) {
      linkElement.setAttribute('aria-disabled', 'true')
    } else {
      linkElement.setAttribute('aria-current', 'page')
    }

    linkElement.addEventListener('click', (e) => e.preventDefault())
  } else {
    if (isNext) {
      linkElement.setAttribute('rel', 'next')
    } else if (isPrevious) {
      linkElement.setAttribute('rel', 'prev')
    }

    linkElement.setAttribute('href', link)
    linkElement.setAttribute('title', `Naviguer vers la page ${textTranslated}`)
  }

  if (isCanonical) {
    linkElement.setAttribute('rel', 'canonical')
  }

  listItemElement.appendChild(linkElement)
  return listItemElement
}

export const generatePagination = (document: Document, currentPage: number, totalPages: number): HTMLElement | null => {
  const nav = getPaginationContainer(document)

  if (!nav) return null

  const ul = nav.querySelector('ul')!
  ul.innerHTML = ''

  if (totalPages <= 1) {
    nav.removeAttribute('aria-labelledby')
    nav.setAttribute('aria-hidden', 'true')
    return nav
  }

  const queries = getQueries()

  ul.appendChild(
    getPaginationItem(document, {
      isPrevious: true,
      isCurrent: currentPage === 1,
      isNextPrevious: true,
      link: `?page=${Math.max(1, currentPage - 1)}${queries}`,
      text: '&lt;',
    }),
  )

  for (let i = 1; i <= totalPages; i++) {
    ul.appendChild(
      getPaginationItem(document, {
        isCanonical: i === 1,
        isCurrent: i === currentPage,
        isNext: i === currentPage + 1,
        isPrevious: i === currentPage - 1,
        link: `?page=${i}${queries}`,
        text: `${i}`,
      }),
    )
  }

  ul.appendChild(
    getPaginationItem(document, {
      isNext: true,
      isCurrent: currentPage === totalPages,
      isNextPrevious: true,
      link: `?page=${Math.min(totalPages, currentPage + 1)}${queries}`,
      text: '&gt;',
    }),
  )

  nav.appendChild(ul)

  return nav
}
